import React, { useCallback, useEffect, useRef, useState } from 'react';
import { message } from 'components/Dialog/Toast';
import { style as imageStyle, url as imageUrl } from 'utils/image';

import './exhibitor-card.scss';

interface ExhibitorInfo {
  id: number;
  name: string;
  short_name?: string;
  logo?: string;
  exhibitor_logo?: string;
  booth?: string;
  description?: string;
  contact_person?: string;
  contact_person_mobile?: string;
  banner?: string;
  [key: string]: any;
}

interface ExhibitorConfig {
  banner?: string;
  defaultBanner?: string;
  search?: boolean;
  showList?: boolean;
  autoShowAll?: boolean;
  list?: any[];
  exhibitorOrder?: number[];
}

interface GalleryConfigDetail {
  default_exhibitor_banner?: string;
  [key: string]: any;
}

const DEFAULT_EXHIBITOR_BANNER_FALLBACK =
  'attachment/6b1f910c2d5a73f71d7e103f0aacb1e5.jpg';
const EXHIBITOR_FORM_SUBMITTED_MESSAGE = 'jfoto:exhibitor-form-submitted';

const resolveFormPath = (value?: string) => {
  const rawValue = String(value || '').trim();
  if (!rawValue) return '';

  if (/^[a-zA-Z0-9_-]+$/.test(rawValue)) {
    return `/g/${rawValue}`;
  }

  try {
    const url = new URL(rawValue, window.location.origin);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return '';
  }
};

const parseWindowJSON = (key: string) => {
  const val = (window as any)[key];
  if (!val) return null;
  if (typeof val === 'string') {
    try {
      return JSON.parse(val);
    } catch {
      return null;
    }
  }
  return val;
};

const parseObjectValue = (value: any) => {
  if (!value) return {};
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
        ? parsed
        : {};
    } catch {
      return {};
    }
  }
  return typeof value === 'object' && !Array.isArray(value) ? value : {};
};

const ExhibitorCard: React.FC<any> = () => {
  const exhibitorInfo: ExhibitorInfo | null = parseWindowJSON('exhibitorInfo');
  const exhibitorConfig: ExhibitorConfig =
    parseWindowJSON('exhibitorConfig') || {};
  const configDetail: GalleryConfigDetail = parseWindowJSON('configDetail') || {};

  const {
    name,
    exhibitor_logo,
    logo,
    booth,
    desc,
    contact_person_mobile,
  } = exhibitorInfo || ({} as ExhibitorInfo);
  const extensions = parseObjectValue(configDetail.extensions_json);
  const leadConfig = parseObjectValue(extensions.exhibitor_lead);
  const leadEnabled =
    leadConfig.enabled === true || Number(leadConfig.enabled) === 1;
  const leadEmail = String(leadConfig.email || '').trim();
  const leadFormUrl = String(leadConfig.form_url || '').trim();
  const hasLeadContact = leadEnabled && !!resolveFormPath(leadFormUrl);

  const boothRef = useRef<HTMLDivElement>(null);
  const [boothExpanded, setBoothExpanded] = useState(false);
  const [boothOverflow, setBoothOverflow] = useState(false);
  const [contactDrawerVisible, setContactDrawerVisible] = useState(false);
  const [contactDrawerClosing, setContactDrawerClosing] = useState(false);
  const contactIframeRef = useRef<HTMLIFrameElement>(null);
  const contactDrawerTimerRef = useRef<number>();
  const contactSuccessTimerRef = useRef<number>();

  const closeContactDrawer = useCallback(() => {
    if (contactDrawerClosing) return;
    setContactDrawerClosing(true);
    window.clearTimeout(contactDrawerTimerRef.current);
    contactDrawerTimerRef.current = window.setTimeout(() => {
      setContactDrawerVisible(false);
      setContactDrawerClosing(false);
    }, 240);
  }, [contactDrawerClosing]);

  useEffect(() => {
    setBoothExpanded(false);
  }, [booth]);

  useEffect(() => {
    const boothElement = boothRef.current;
    if (!boothElement) {
      setBoothOverflow(false);
      return;
    }

    setBoothOverflow(boothElement.scrollHeight > boothElement.clientHeight + 1);
  }, [booth, boothExpanded]);

  useEffect(() => {
    if (!contactDrawerVisible) return;

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';

    const onMessage = (event: MessageEvent) => {
      if (
        event.origin === window.location.origin &&
        event.source === contactIframeRef.current?.contentWindow &&
        event.data &&
        event.data.type === EXHIBITOR_FORM_SUBMITTED_MESSAGE
      ) {
        closeContactDrawer();
        window.clearTimeout(contactSuccessTimerRef.current);
        contactSuccessTimerRef.current = window.setTimeout(() => {
          message('提交成功');
        }, 260);
      }
    };

    window.addEventListener('message', onMessage);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('message', onMessage);
    };
  }, [contactDrawerVisible, closeContactDrawer]);

  useEffect(() => {
    return () => {
      window.clearTimeout(contactDrawerTimerRef.current);
      window.clearTimeout(contactSuccessTimerRef.current);
    };
  }, []);

  useEffect(() => {
    if (!hasLeadContact) return;
    document.documentElement.classList.add('exhibitor-contact-enabled');
    return () => {
      document.documentElement.classList.remove('exhibitor-contact-enabled');
    };
  }, [hasLeadContact]);

  if (!exhibitorInfo) return null;

  // banner 回退顺序：展商单独头图 -> 相册全局默认头图 -> 后端注入默认头图 -> 固定兜底图
  const bannerKey =
    exhibitorInfo.gallery_pagedata_banner ||
    configDetail.default_exhibitor_banner ||
    exhibitorConfig.defaultBanner ||
    DEFAULT_EXHIBITOR_BANNER_FALLBACK;
  const bannerSrc = bannerKey ? imageStyle(bannerKey, 'limit', { w: 750 }) : '';
  const logoKey = [exhibitor_logo, logo].find(
    (item) => typeof item === 'string' && item.trim()
  ) || '';
  const logoSrc = logoKey
    ? imageUrl(logoKey, {
        'x-oss-process':
          'image/resize,m_pad,w_400,h_400,color_ffffff/format,jpg',
      }, 1)
    : '';

  const themeMainColor =
    (configDetail && configDetail.theme && configDetail.theme.main) || '#24d9cd';

  const handleContact = () => {
    if (contact_person_mobile) {
      window.location.href = `tel:${contact_person_mobile}`;
    }
  };

  const handleMessage = () => {
    if (leadFormUrl) {
      setContactDrawerClosing(false);
      setContactDrawerVisible(true);
    }
  };

  const contactFormUrl = (() => {
    const formPath = resolveFormPath(leadFormUrl);
    if (!formPath) return '';

    const url = new URL(formPath, window.location.origin);
    url.searchParams.set('embedded_exhibitor_form', '1');
    if (window.pageDetail && window.pageDetail.link_id) {
      url.searchParams.set('embedded_parent_linkid', window.pageDetail.link_id);
    }
    if (leadEmail) url.searchParams.set('email_to', leadEmail);
    url.searchParams.set('hidden_来源展商', name || '');
    url.searchParams.set('hidden_来源展商ID', String(exhibitorInfo.id));
    url.searchParams.set('hidden_来源展位', booth || '');
    return `${url.pathname}${url.search}${url.hash}`;
  })();
  const showCustomContactForm =
    leadEnabled &&
    !!contactFormUrl;

  const handleBack = () => {
    if (window.history.length > 1) {
      window.history.back();
    }
  };

  return (
    <>
      <div className="exhibitor-card-component">
        {/* Banner 区域 */}
        <div className="exhibitor-banner">
          {bannerSrc ? (
            <img src={bannerSrc} alt="" />
          ) : (
            <div className="banner-placeholder" />
          )}
        </div>

        {/* 信息卡片 */}
        <div className="exhibitor-info-card">
          <div className="exhibitor-card-head">
            {logoSrc ? (
              <div className="exhibitor-logo">
                <img src={logoSrc} alt={name} className="caowei" />
              </div>
            ) : null}
            <div className="exhibitor-card-main">
              <div className="exhibitor-card-name">{name}</div>
              {booth && (
                <div className="exhibitor-card-booth-block">
                  <div className="exhibitor-card-booth-label">展位号</div>
                  <div
                    ref={boothRef}
                    className={
                      boothExpanded
                        ? 'exhibitor-card-booth expanded'
                        : 'exhibitor-card-booth'
                    }
                  >
                    {booth}
                  </div>
                  {boothOverflow && !boothExpanded ? (
                    <button
                      type="button"
                      className="exhibitor-card-booth-expand"
                      style={{ color: themeMainColor }}
                      onClick={() => setBoothExpanded(true)}
                    >
                      展开全部展位
                    </button>
                  ) : null}
                </div>
              )}
            </div>
          </div>
          {desc && <div className="exhibitor-card-desc">{desc}</div>}
          {contact_person_mobile ? (
            <div className="exhibitor-card-actions">
              <div
                className="exhibitor-card-contact"
                style={{ color: themeMainColor }}
                onClick={handleContact}
              >
                <svg className="contact-icon" viewBox="0 0 1024 1024" aria-hidden="true">
                  <path
                    fill="currentColor"
                    d="M704 896c-148.5 0-291.2-58.9-396.2-163.8S144 484.5 144 336c0-88.4 71.6-160 160-160 20.4 0 38.7 12.5 46.1 31.5l64 164c7.3 18.6 2.5 39.8-12.1 53.4l-61.7 57.6c39.8 77.2 103 140.4 180.2 180.2l57.6-61.7c13.6-14.6 34.8-19.4 53.4-12.1l164 64c19 7.4 31.5 25.7 31.5 46.1 0 88.4-71.6 160-160 160z"
                  />
                </svg>
                <span>联系展商</span>
              </div>
            </div>
          ) : null}
        </div>
      </div>

      {/* 返回按钮 */}
      <div className="exhibitor-back-btn" onClick={handleBack}>
        <span className="back-icon">‹</span>
        <span>返回展商列表</span>
      </div>

      {showCustomContactForm ? (
        <button
          type="button"
          className="exhibitor-custom-contact-button"
          style={{ backgroundColor: themeMainColor }}
          onClick={handleMessage}
        >
          联系展商
        </button>
      ) : null}

      {contactDrawerVisible && showCustomContactForm ? (
        <div
          className={`exhibitor-contact-drawer ${contactDrawerClosing ? 'closing' : ''}`}
          role="dialog"
          aria-modal="true"
        >
          <div
            className="exhibitor-contact-drawer-mask"
            onClick={closeContactDrawer}
          />
          <div className="exhibitor-contact-drawer-panel">
            <div className="exhibitor-contact-drawer-handle" />
            <button
              type="button"
              className="exhibitor-contact-drawer-close"
              aria-label="关闭"
              onClick={closeContactDrawer}
            >
              ×
            </button>
            <iframe
              ref={contactIframeRef}
              title="联系展商"
              src={contactFormUrl}
              frameBorder="0"
              allow="clipboard-write"
            />
          </div>
        </div>
      ) : null}
    </>
  );
};

export default ExhibitorCard;
